import * as React from 'react';
import { Button, DrawerBody, makeStyles, MenuDivider, Subtitle2, tokens } from '@fluentui/react-components';
import { Open20Regular } from '@fluentui/react-icons';
import { defineMessage, useIntl } from 'react-intl';
import { useUserQuery } from '@foundershub/api-client';
import { TextLink } from '@foundershub/components';
import { DrawerContext } from '../../components/FoundersHubUserOverlayDrawer';
import { DrawerHeading } from '../../drawers/DrawerHeading';
import { UserSupportForm } from './UserSupportForm';

export default function SupportDrawer() {
  const intl = useIntl();
  const classes = useStyles();
  const { isError: isUserLoggedOut } = useUserQuery();
  const { setShowDrawer, setChildren } = React.useContext(DrawerContext);

  function closeDrawer() {
    setShowDrawer(false);
    setChildren(null);
  }

  function openSupportTicketForm() {
    setChildren(<UserSupportForm onBackButtonClick={() => setChildren(<SupportDrawer />)} />);
  }

  return (
    <>
      <DrawerHeading title={intl.formatMessage(titleLabel)} closeDrawer={closeDrawer} />
      <DrawerBody>
        <div className={classes.drawerBody}>
          <Subtitle2>{intl.formatMessage(resourcesLabel)}</Subtitle2>
          {isUserLoggedOut && (
            <TextLink href="https://aka.ms/startuphelp-applying" target="_blank" rel="noopener noreferrer">
              {intl.formatMessage(applyingLabel)}
            </TextLink>
          )}
          <TextLink
            className={classes.helpLink}
            href="https://aka.ms/startuphelp-benefits"
            target="_blank"
            rel="noopener noreferrer"
          >
            {intl.formatMessage(programBenefitsLabel)}

            <Open20Regular />
          </TextLink>
          <TextLink
            className={classes.helpLink}
            href="https://aka.ms/startuphelp-mfs-portal"
            target="_blank"
            rel="noopener noreferrer"
          >
            {intl.formatMessage(mfsPortalLabel)}

            <Open20Regular />
          </TextLink>
          <TextLink
            className={classes.helpLink}
            href="https://aka.ms/startuphelp-other"
            target="_blank"
            rel="noopener noreferrer"
          >
            {intl.formatMessage(otherLabel)}
            <Open20Regular />
          </TextLink>

          {!isUserLoggedOut && (
            <>
              <MenuDivider />
              <span>{intl.formatMessage(needMoreHelpLabel)}</span>
              <Button appearance="primary" className={classes.button} onClick={() => openSupportTicketForm()}>
                {intl.formatMessage(supportTicketButtonLabel)}
              </Button>
            </>
          )}
        </div>
      </DrawerBody>
    </>
  );
}

const titleLabel = defineMessage({
  defaultMessage: 'Help',
  description: 'support drawer title',
});

const resourcesLabel = defineMessage({
  defaultMessage: 'Resources',
  description: 'header for resources section',
});

const applyingLabel = defineMessage({
  defaultMessage: 'Applying',
  description: 'nav link to application information',
});

const programBenefitsLabel = defineMessage({
  defaultMessage: 'Program benefits',
  description: 'nav link to program benefits information',
});

const mfsPortalLabel = defineMessage({
  defaultMessage: 'Microsoft for Startups portal',
  description: '{Locked="Microsoft for Startups"}, nav link to Microsoft for Startups portal',
});

const otherLabel = defineMessage({
  defaultMessage: 'Other',
  description: 'nav link to other information',
});

const needMoreHelpLabel = defineMessage({
  defaultMessage: 'Need more help?',
  description: 'header for need more help section',
});

const supportTicketButtonLabel = defineMessage({
  defaultMessage: 'Submit a Support Ticket',
  description: 'button to submit a support ticket',
});

const useStyles = makeStyles({
  button: {
    width: '50%',
  },
  drawerBody: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalS,
  },
  helpLink: {
    display: 'flex',
    columnGap: tokens.spacingHorizontalXS,
  },
});
